// analysis/PremiumResult.tsx
//
// Premium hair / makeup RESULT screen — a faithful React port of the Kivo
// premium-analysis prototype (kivo/hair.jsx, kivo/makeup.jsx, kivo/ui.jsx,
// kivo/core.jsx). This is the in-app scrolling result composition: native
// editorial text / swatches / meters / pills, the user's real photo cells
// cropped from the generated atlas, a drag-to-compare before/after, and
// tap-to-copy hex swatches.
//
// The print "poster" one-sheet lives in HairAnalysisPDFPage / MakeupAnalysisPDFPage.
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { AtlasCell } from '../atlas/AtlasCell';
import { cellTrim, type AtlasCoords } from '../atlas/coords';
import { AuthImage } from '../components/AuthImage';
import type { HairAnalysisResult, MakeupAnalysisResult } from '../types/api';

/* ── Light theme (kivo direction A) ────────────────────────────────────── */
const T = {
  f: {
    display: "'Fraunces', Georgia, serif",
    text: "'Inter', -apple-system, system-ui, sans-serif",
    mono: "'Spline Sans Mono','SF Mono',ui-monospace,monospace",
  },
  cream: '#F8F4EE', sand: '#EFE7DC',
  surface: '#FFFFFF', surface2: '#FBF8F4',
  ink: '#1F2421', ink70: 'rgba(31,36,33,.7)', ink45: 'rgba(31,36,33,.45)',
  rule: 'rgba(31,36,33,.1)',
  accentBase: '#2AA4A6', accentDeep: '#1A6F70', accentInk: '#1A6F70',
  accentSoft: 'rgba(42,164,166,.14)',
  coral: '#F4A88E', coralDeep: '#B6663F',
  shSoft: '0 2px 8px rgba(31,36,33,.05),0 12px 30px rgba(31,36,33,.07)',
  radius: { sm: 10, md: 16, lg: 24, xl: 30 },
};
const hexA = (hex: string, a: number) => {
  const h = hex.replace('#', '');
  const n = Number.parseInt(h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};
const prefersReducedMotion = () =>
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ── Icons ─────────────────────────────────────────────────────────────── */
type IP = { s?: number };
const I = {
  spark: (p: IP = {}) => <svg viewBox="0 0 24 24" width={p.s ?? 18} height={p.s ?? 18} fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v4M12 17v4M3 12h4M17 12h4M12 12l4.5-4.5M12 12 7.5 16.5M12 12l4.5 4.5M12 12 7.5 7.5" /></svg>,
  dl: (p: IP = {}) => <svg viewBox="0 0 24 24" width={p.s ?? 16} height={p.s ?? 16} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>,
  share: (p: IP = {}) => <svg viewBox="0 0 24 24" width={p.s ?? 16} height={p.s ?? 16} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13" /></svg>,
  copy: (p: IP = {}) => <svg viewBox="0 0 24 24" width={p.s ?? 13} height={p.s ?? 13} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>,
  check: (p: IP = {}) => <svg viewBox="0 0 24 24" width={p.s ?? 13} height={p.s ?? 13} fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>,
  x: (p: IP = {}) => <svg viewBox="0 0 24 24" width={p.s ?? 13} height={p.s ?? 13} fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>,
  drag: (p: IP = {}) => <svg viewBox="0 0 24 24" width={p.s ?? 18} height={p.s ?? 18} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M8 7 4 11l4 4M16 7l4 4-4 4" /></svg>,
  hair: (p: IP = {}) => <svg viewBox="0 0 24 24" width={p.s ?? 18} height={p.s ?? 18} fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><path d="M4 13a8 8 0 0 1 16 0M4 13c0 4 2 7 2 7M20 13c0 4-2 7-2 7M9 13.5c0 3-1 6.5-1 6.5M15 13.5c0 3 1 6.5 1 6.5M12 13v7" /></svg>,
  makeup: (p: IP = {}) => <svg viewBox="0 0 24 24" width={p.s ?? 18} height={p.s ?? 18} fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><path d="M3 12s3.5-5 9-5 9 5 9 5-3.5 5-9 5-9-5-9-5Z" /><circle cx="12" cy="12" r="2.4" /></svg>,
  eye: (p: IP = {}) => <svg viewBox="0 0 24 24" width={p.s ?? 16} height={p.s ?? 16} fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z" /><circle cx="12" cy="12" r="2.6" /></svg>,
  lips: (p: IP = {}) => <svg viewBox="0 0 24 24" width={p.s ?? 16} height={p.s ?? 16} fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><path d="M3 11s2-3 4-3 3 1.5 5 1.5S18 8 20 8s1 3 1 3-3 5-9 5-9-5-9-5Z" /></svg>,
};

/* ── Reveal on scroll ──────────────────────────────────────────────────── */
function Reveal({ children, delay = 0, y = 18, style }: { children: React.ReactNode; delay?: number; y?: number; style?: React.CSSProperties }) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (prefersReducedMotion()) { setShown(true); return; }
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver((es) => {
      for (const e of es) { if (e.isIntersecting) { setShown(true); io.disconnect(); } }
    }, { threshold: 0.01 });
    io.observe(el);
    const fb = setTimeout(() => { setShown(true); }, 650 + delay);
    return () => { io.disconnect(); clearTimeout(fb); };
  }, [delay]);
  return (
    <div ref={ref} style={{
      opacity: shown ? 1 : 0,
      transform: shown ? 'none' : `translateY(${y}px)`,
      transition: `opacity .6s cubic-bezier(.22,.61,.36,1) ${delay}ms, transform .6s cubic-bezier(.22,.61,.36,1) ${delay}ms`,
      ...style,
    }}>{children}</div>
  );
}

/* ── Section head ──────────────────────────────────────────────────────── */
function SectionHead({ index, title, kicker }: { index?: string; title: string; kicker?: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {index != null && <span style={{ font: `500 10px/1 ${T.f.mono}`, letterSpacing: '.2em', color: T.accentInk }}>{index}</span>}
        <span style={{ flex: 1, height: 1, background: T.rule }} />
        {kicker && <span style={{ font: `500 10px/1 ${T.f.text}`, letterSpacing: '.18em', textTransform: 'uppercase', color: T.ink45 }}>{kicker}</span>}
      </div>
      <h2 style={{ margin: 0, font: `400 26px/1.05 ${T.f.display}`, letterSpacing: '-.015em', color: T.ink }}>{title}</h2>
    </div>
  );
}

function Card({ children, pad = 18, style }: { children: React.ReactNode; pad?: number; style?: React.CSSProperties }) {
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.rule}`, borderRadius: T.radius.lg, boxShadow: T.shSoft, padding: pad, ...style }}>
      {children}
    </div>
  );
}

function MiniLabel({ children, bad }: { children: React.ReactNode; bad?: boolean }) {
  return <div style={{ font: `500 10px/1 ${T.f.text}`, letterSpacing: '.16em', textTransform: 'uppercase', color: bad ? T.coralDeep : T.ink45, marginBottom: 14 }}>{children}</div>;
}

/* ── Tap-to-copy swatch ────────────────────────────────────────────────── */
function Swatch({ name, hex, size = 46 }: { name: string; hex: string; size?: number }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if ('clipboard' in navigator) {
      void navigator.clipboard.writeText(hex).catch(() => { /* ignore copy failures */ });
    }
    setCopied(true);
    setTimeout(() => { setCopied(false); }, 1100);
  };
  return (
    <button onClick={copy} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7, background: 'none', border: 'none', cursor: 'pointer', padding: 0, width: size + 18 }}>
      <div style={{ position: 'relative', width: size, height: size }}>
        <div style={{ width: size, height: size, borderRadius: '50%', background: hex, boxShadow: `0 3px 10px ${hexA(hex, .45)}, inset 0 0 0 2px rgba(255,255,255,.5)`, border: `1px solid ${T.rule}`, transition: 'transform .2s cubic-bezier(.22,.61,.36,1)', transform: copied ? 'scale(.9)' : 'scale(1)' }} />
        <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', opacity: copied ? 1 : 0, transition: 'opacity .2s', pointerEvents: 'none', textShadow: '0 1px 3px rgba(0,0,0,.4)' }}>{I.check({ s: 16 })}</div>
      </div>
      <span style={{ font: `500 11px/1.2 ${T.f.text}`, color: T.ink70, textAlign: 'center' }}>{name}</span>
      <span style={{ font: `500 9px/1 ${T.f.mono}`, color: copied ? T.accentInk : T.ink45, letterSpacing: '.04em', display: 'inline-flex', alignItems: 'center', gap: 3, textTransform: 'uppercase' }}>
        {copied ? 'copied' : <>{I.copy({ s: 9 })}{hex}</>}
      </span>
    </button>
  );
}
function SwatchRow({ items, size }: { items: { name: string; hex: string }[]; size?: number }) {
  return <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px 6px' }}>{items.map((c, i) => <Swatch key={i} name={c.name} hex={c.hex} size={size} />)}</div>;
}

/* ── Mark list (checks / x) ────────────────────────────────────────────── */
function MarkList({ items, bad }: { items: string[]; bad?: boolean }) {
  const col = bad ? T.coralDeep : T.accentInk;
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 11 }}>
      {items.map((it, i) => (
        <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <span style={{ color: col, marginTop: 1, flexShrink: 0, display: 'inline-flex' }}>{bad ? I.x({ s: 14 }) : I.check({ s: 14 })}</span>
          <span style={{ font: `400 14px/1.5 ${T.f.text}`, color: T.ink70 }}>{it}</span>
        </li>
      ))}
    </ul>
  );
}

/* ── Meter ─────────────────────────────────────────────────────────────── */
function Meter({ label, value, position, stops }: { label: string; value: string; position: number; stops: string[] }) {
  const pos = Math.max(0, Math.min(1, position));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ font: `500 10px/1 ${T.f.text}`, letterSpacing: '.16em', textTransform: 'uppercase', color: T.ink45 }}>{label}</span>
        <span style={{ font: `400 14px/1 ${T.f.display}`, color: T.ink }}>{value}</span>
      </div>
      <div style={{ position: 'relative', height: 14, borderRadius: 999, overflow: 'hidden', background: `linear-gradient(90deg, ${stops.join(',')})`, border: `1px solid ${T.rule}` }} />
      <div style={{ position: 'relative', height: 0 }}>
        <div style={{ position: 'absolute', top: -21, left: `${pos * 100}%`, transform: 'translateX(-50%)', width: 6, height: 18, borderRadius: 999, background: T.accentBase, boxShadow: `0 1px 4px rgba(0,0,0,.3), 0 0 0 3px ${T.cream}` }} />
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ flex: 1, minWidth: 0, padding: '14px 14px', borderRadius: T.radius.md, background: T.surface2, border: `1px solid ${T.rule}` }}>
      <div style={{ font: `500 9px/1.2 ${T.f.text}`, letterSpacing: '.14em', textTransform: 'uppercase', color: T.ink45, marginBottom: 7 }}>{label}</div>
      <div style={{ font: `400 16px/1.15 ${T.f.display}`, color: T.ink, letterSpacing: '-.01em' }}>{value}</div>
    </div>
  );
}

function FinishBar({ icon, label, value }: { icon?: React.ReactNode; label: string; value: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: T.radius.md, background: T.accentSoft }}>
      {icon && <span style={{ color: T.accentInk, display: 'inline-flex' }}>{icon}</span>}
      <span style={{ font: `400 13px/1 ${T.f.display}`, fontStyle: 'italic', color: T.accentInk }}>{label}</span>
      <span style={{ flex: 1, height: 1, background: hexA(T.accentBase, .25) }} />
      <span style={{ font: `500 13px/1 ${T.f.text}`, color: T.ink }}>{value}</span>
    </div>
  );
}

/* ── Atlas image fill (covers its positioned parent) ───────────────────── */
function AtlasFill({ atlasUrl, coords, cellId, targetAspect, style }: {
  atlasUrl: string; coords: AtlasCoords; cellId: string; targetAspect: number; style?: React.CSSProperties;
}) {
  if (!(cellId in coords.cells)) return null;
  const cell = coords.cells[cellId];
  const trim = cellTrim(coords);
  const cropX = cell.x + trim;
  const cropY = cell.y + trim;
  const cropW = cell.w - 2 * trim;
  const cropH = cell.h - 2 * trim;
  const scale = Math.max(targetAspect / cropW, 1 / cropH);
  const renderedW = coords.canvas.w * scale;
  const renderedH = coords.canvas.h * scale;
  const offsetX = (targetAspect - cropW * scale) / 2 - cropX * scale;
  const offsetY = (1 - cropH * scale) / 2 - cropY * scale;
  const positionX = targetAspect === renderedW ? 0 : (offsetX / (targetAspect - renderedW)) * 100;
  const positionY = renderedH === 1 ? 0 : (offsetY / (1 - renderedH)) * 100;
  const coverStyle: React.CSSProperties | null = atlasUrl
    ? {
        backgroundImage: `url(${atlasUrl})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: `${(renderedW / targetAspect) * 100}% ${renderedH * 100}%`,
        backgroundPosition: `${positionX}% ${positionY}%`,
      }
    : null;

  return (
    <div
      data-testid={`atlas-fill-${cellId}`}
      style={{ position: 'absolute', inset: 0, background: coverStyle ? undefined : T.sand, ...(coverStyle ?? {}), ...style }}
    />
  );
}

/* ── Zoom lightbox (screen-centered, portaled to <body>) ───────────────── */
function CellLightbox({ atlasUrl, coords, cellId, imagePath, caption, onClose }: {
  atlasUrl: string; coords: AtlasCoords; cellId?: string; imagePath?: string; caption?: string; onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); };
  }, [onClose]);
  if (typeof document === 'undefined') return null;
  return ReactDOM.createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Image preview"
      style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'grid', placeItems: 'center', padding: 24, background: 'rgba(20,24,22,.74)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', border: 'none', cursor: 'pointer' }}
    >
      <div style={{ position: 'relative', width: 'min(90vw, 460px)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
        <div style={{ width: '100%', maxHeight: '80vh', overflow: 'hidden', borderRadius: T.radius.lg, boxShadow: '0 20px 60px rgba(0,0,0,.45)' }}>
          {imagePath
            ? <AuthImage path={imagePath} alt={caption ?? 'Photo preview'} style={{ display: 'block', width: '100%', maxHeight: '80vh', objectFit: 'contain', background: T.sand }} />
            : cellId
              ? <AtlasCell atlasUrl={atlasUrl} coords={coords} cellId={cellId} radius={T.radius.lg} style={{ maxHeight: '80vh' }} />
              : null}
        </div>
        {caption && <span style={{ font: `400 14px/1.4 ${T.f.text}`, color: T.cream, textAlign: 'center' }}>{caption}</span>}
        <button onClick={onClose} aria-label="Close" style={{ position: 'absolute', top: -12, right: -12, width: 36, height: 36, borderRadius: '50%', border: 'none', cursor: 'pointer', background: T.cream, color: T.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 14px rgba(0,0,0,.3)' }}>
          {I.x({ s: 16 })}
        </button>
      </div>
    </div>,
    document.body,
  );
}

/* A cropped atlas cell that opens a zoomed lightbox on tap. */
function ZoomableAtlasCell({ atlasUrl, coords, cellId, caption, radius = 10, style }: {
  atlasUrl: string; coords: AtlasCoords; cellId: string; caption?: string; radius?: number | string; style?: React.CSSProperties;
}) {
  const [open, setOpen] = useState(false);
  if (!(cellId in coords.cells)) return null;
  if (!atlasUrl) return <AtlasCell atlasUrl={atlasUrl} coords={coords} cellId={cellId} radius={radius} style={style} />;
  return (
    <>
      <button
        type="button"
        onClick={() => { setOpen(true); }}
        title="Tap to zoom"
        style={{ display: 'block', width: '100%', padding: 0, border: 'none', background: 'none', cursor: 'zoom-in' }}
      >
        <AtlasCell atlasUrl={atlasUrl} coords={coords} cellId={cellId} radius={radius} style={style} />
      </button>
      {open && <CellLightbox atlasUrl={atlasUrl} coords={coords} cellId={cellId} caption={caption} onClose={() => { setOpen(false); }} />}
    </>
  );
}

/* ── Before / after slider — both sides are user-selectable ────────────── */
export type CompareOption = { id: string; label: string; cellId?: string; photo?: boolean };

function pickCompareOption(options: CompareOption[], id: string, fallback: 'first' | 'last'): CompareOption {
  const match = options.find((o) => o.id === id);
  if (match) return match;
  if (options.length === 0) return { id: 'none', label: 'None' };
  if (fallback === 'last') {
    const lastIndex = options.length - 1;
    return options[lastIndex];
  }
  return options[0];
}

function lastCompareId(options: CompareOption[]): string {
  const lastIndex = options.length - 1;
  return options[lastIndex].id;
}

function ComparePicker({ tone, label, options, value, onChange }: {
  tone: 'a' | 'b'; label: string; options: CompareOption[]; value: string; onChange: (id: string) => void;
}) {
  const dot = tone === 'a' ? T.accentBase : T.coralDeep;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7, minWidth: 0, flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: dot, flexShrink: 0 }} />
        <span style={{ font: `500 9px/1 ${T.f.text}`, letterSpacing: '.16em', textTransform: 'uppercase', color: T.ink45 }}>{label}</span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {options.map(o => {
          const on = o.id === value;
          return (
            <button key={o.id} type="button" onClick={() => { onChange(o.id); }}
              style={{
                font: `500 11px/1 ${T.f.text}`, padding: '7px 11px', borderRadius: 999, cursor: 'pointer',
                border: `1px solid ${on ? hexA(dot, .9) : T.rule}`,
                background: on ? hexA(dot, .12) : T.surface,
                color: on ? T.ink : T.ink70, whiteSpace: 'nowrap', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CompareSide({ opt, atlasUrl, coords, imagePath, targetAspect, width }: {
  opt: CompareOption; atlasUrl: string; coords: AtlasCoords; imagePath?: string; targetAspect: number; width?: number | string;
}) {
  if (opt.photo && imagePath) {
    return <AuthImage path={imagePath} alt={opt.label} style={{ width: width ?? '100%', height: '100%', objectFit: 'cover', display: 'block' }} />;
  }
  if (opt.cellId) {
    return (
      <div style={{ position: 'absolute', inset: 0, width: width ?? '100%', overflow: 'hidden' }}>
        <AtlasFill atlasUrl={atlasUrl} coords={coords} cellId={opt.cellId} targetAspect={targetAspect} />
      </div>
    );
  }
  return <div style={{ position: 'absolute', inset: 0, background: T.sand }} />;
}

function BeforeAfter({ options, defaultBeforeId, defaultAfterId, atlasUrl, coords, imagePath, ratio = 0.82 }: {
  options: CompareOption[]; defaultBeforeId: string; defaultAfterId: string;
  atlasUrl: string; coords: AtlasCoords; imagePath?: string; ratio?: number;
}) {
  const wrap = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState(50);
  const [beforeId, setBeforeId] = useState(defaultBeforeId);
  const [afterId, setAfterId] = useState(defaultAfterId);
  const [preview, setPreview] = useState<CompareOption | null>(null);
  const before = pickCompareOption(options, beforeId, 'first');
  const after = pickCompareOption(options, afterId, 'last');
  const drag = useRef(false);
  const move = (clientX: number) => {
    const el = wrap.current; if (!el) return;
    const r = el.getBoundingClientRect();
    setPos(Math.max(2, Math.min(98, ((clientX - r.left) / r.width) * 100)));
  };
  useEffect(() => {
    const mm = (e: MouseEvent | TouchEvent) => { if (drag.current) move('touches' in e ? e.touches[0].clientX : e.clientX); };
    const mu = () => { drag.current = false; };
    window.addEventListener('mousemove', mm); window.addEventListener('mouseup', mu);
    window.addEventListener('touchmove', mm, { passive: false }); window.addEventListener('touchend', mu);
    return () => { window.removeEventListener('mousemove', mm); window.removeEventListener('mouseup', mu); window.removeEventListener('touchmove', mm); window.removeEventListener('touchend', mu); };
  }, []);
  const badge = (side: 'left' | 'right', opt: CompareOption) => (
    <button
      type="button"
      aria-label={`Open ${opt.label}`}
      onMouseDown={(e) => { e.stopPropagation(); }}
      onTouchStart={(e) => { e.stopPropagation(); }}
      onClick={(e) => { e.stopPropagation(); setPreview(opt); }}
      style={{
        position: 'absolute', zIndex: 4, top: 12, [side]: 12, display: 'inline-flex', alignItems: 'center', gap: 5,
        maxWidth: '42%', overflow: 'hidden', border: 'none', cursor: 'zoom-in',
        font: `500 9px/1 ${T.f.mono}`, letterSpacing: '.12em', textTransform: 'uppercase',
        color: '#fff', background: 'rgba(20,24,22,.62)', backdropFilter: 'blur(6px)',
        padding: '6px 9px', borderRadius: 999, textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{opt.label}</span>
      <span aria-hidden="true" style={{ flexShrink: 0, fontSize: 11 }}>↗</span>
    </button>
  );
  const innerW = wrap.current ? wrap.current.getBoundingClientRect().width : undefined;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div ref={wrap}
        role="slider"
        aria-label="Before and after comparison"
        aria-valuemin={2}
        aria-valuemax={98}
        aria-valuenow={Math.round(pos)}
        tabIndex={0}
        style={{ position: 'relative', width: '100%', aspectRatio: String(ratio), borderRadius: T.radius.lg, overflow: 'hidden', border: `1px solid ${T.rule}`, boxShadow: T.shSoft, cursor: 'ew-resize', userSelect: 'none', touchAction: 'none', background: T.sand }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') setPos((p) => Math.max(2, p - 2));
          if (e.key === 'ArrowRight') setPos((p) => Math.min(98, p + 2));
        }}
        onMouseDown={e => { drag.current = true; move(e.clientX); }}
        onTouchStart={e => { drag.current = true; move(e.touches[0].clientX); }}>
        {/* after (full, beneath) */}
        <div style={{ position: 'absolute', inset: 0 }}>
          <CompareSide opt={after} atlasUrl={atlasUrl} coords={coords} imagePath={imagePath} targetAspect={ratio} />
        </div>
        {/* before (clipped, on top) */}
        <div style={{ position: 'absolute', inset: 0, width: `${pos}%`, overflow: 'hidden', borderRight: `2px solid #fff` }}>
          <div style={{ position: 'absolute', inset: 0, width: innerW ?? '100vw', height: '100%' }}>
            <CompareSide opt={before} atlasUrl={atlasUrl} coords={coords} imagePath={imagePath} targetAspect={ratio} width={innerW ?? '100%'} />
          </div>
        </div>
        {/* handle */}
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${pos}%`, transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', pointerEvents: 'none' }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#fff', color: T.accentInk, boxShadow: '0 4px 14px rgba(0,0,0,.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{I.drag({ s: 18 })}</div>
        </div>
        {badge('left', before)}
        {badge('right', after)}
      </div>
      {preview && (
        <CellLightbox
          atlasUrl={atlasUrl}
          coords={coords}
          cellId={preview.cellId}
          imagePath={preview.photo ? imagePath : undefined}
          caption={preview.label}
          onClose={() => { setPreview(null); }}
        />
      )}
      {options.length > 1 && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <ComparePicker tone="a" label="Before" options={options} value={before.id} onChange={setBeforeId} />
          <ComparePicker tone="b" label="After" options={options} value={after.id} onChange={setAfterId} />
        </div>
      )}
    </div>
  );
}

/* ── Versus grid (flattering vs less, from atlas crops) ────────────────── */
type VsCell = { cellId: string; caption?: string };
function VersusGrid({ atlasUrl, coords, aTitle, aCells, bTitle, bCells }: {
  atlasUrl: string; coords: AtlasCoords; aTitle: string; aCells: VsCell[]; bTitle: string; bCells: VsCell[];
}) {
  const col = (title: string, cells: VsCell[], bad: boolean) => (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12 }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: bad ? T.coralDeep : T.accentBase, flexShrink: 0 }} />
        <span style={{ font: `500 10px/1.1 ${T.f.text}`, letterSpacing: '.12em', textTransform: 'uppercase', color: T.ink70 }}>{title}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {cells.map((c) => (
          <div key={c.cellId} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <div style={{ opacity: bad ? 0.82 : 1 }}>
              <ZoomableAtlasCell atlasUrl={atlasUrl} coords={coords} cellId={c.cellId} caption={c.caption} radius={10} style={{ border: `1px solid ${T.rule}` }} />
            </div>
            {c.caption && <span style={{ font: `400 10.5px/1.25 ${T.f.text}`, color: T.ink70, textAlign: 'center' }}>{c.caption}</span>}
          </div>
        ))}
      </div>
    </div>
  );
  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      {col(aTitle, aCells, false)}
      <div style={{ width: 1, alignSelf: 'stretch', background: T.rule }} />
      {col(bTitle, bCells, true)}
    </div>
  );
}

/* ── Hero ──────────────────────────────────────────────────────────────── */
function Hero({ kind, title, em, id, date, imagePath, atlasUrl, coords, stats }: {
  kind: 'hair' | 'makeup'; title: string; em: string; id: string; date: string;
  imagePath?: string; atlasUrl: string; coords: AtlasCoords; stats: { label: string; value: string }[];
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ font: `600 19px/1 ${T.f.text}`, letterSpacing: '-.02em', color: T.ink, display: 'inline-flex', alignItems: 'baseline' }}>
          kivo<span style={{ width: 6, height: 6, borderRadius: '50%', background: T.accentBase, display: 'inline-block', marginLeft: 1 }} />
        </span>
        <span style={{ font: `500 9px/1 ${T.f.mono}`, letterSpacing: '.14em', textTransform: 'uppercase', color: T.ink45 }}>#{id} · {date}</span>
      </div>
      <div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
          <span style={{ color: T.accentInk, display: 'inline-flex' }}>{kind === 'hair' ? I.hair({ s: 15 }) : I.makeup({ s: 15 })}</span>
          <span style={{ font: `500 10px/1 ${T.f.text}`, letterSpacing: '.2em', textTransform: 'uppercase', color: T.accentInk }}>{kind === 'hair' ? 'Hair' : 'Makeup'} Analysis</span>
        </div>
        <h1 style={{ margin: 0, font: `400 42px/.98 ${T.f.display}`, letterSpacing: '-.02em', color: T.ink }}>
          {title}<br /><em style={{ fontStyle: 'italic', color: T.accentInk }}>{em}</em>
        </h1>
      </div>
      <div style={{ display: 'flex', gap: 12 }}>
        <div style={{ width: '40%', flexShrink: 0 }}>
          <div style={{ position: 'relative', width: '100%', aspectRatio: '0.82', borderRadius: T.radius.lg, overflow: 'hidden', border: `1px solid ${T.rule}`, background: T.sand }}>
            {imagePath
              ? <AuthImage path={imagePath} alt="your photo" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              : <AtlasFill atlasUrl={atlasUrl} coords={coords} cellId="A1" targetAspect={0.82} />}
          </div>
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center' }}>
          {stats.map((s, i) => <Stat key={i} label={s.label} value={s.value} />)}
        </div>
      </div>
    </div>
  );
}

/* ── CTA ───────────────────────────────────────────────────────────────── */
function CTA({ onOpenPoster, busy = false }: { onOpenPoster?: () => void; busy?: boolean }) {
  return (
    <Reveal>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center', textAlign: 'center', padding: '28px 18px', borderRadius: T.radius.xl, background: T.accentDeep, color: T.cream }}>
        <span style={{ color: T.cream, display: 'inline-flex' }}>{I.spark({ s: 22 })}</span>
        <h3 style={{ margin: 0, font: `400 24px/1.05 ${T.f.display}`, color: T.cream }}>Keep your report</h3>
        <p style={{ margin: 0, font: `400 13px/1.5 ${T.f.text}`, color: 'rgba(248,244,238,.7)', maxWidth: 240 }}>
          Download the editorial one-sheet — crisp text, sharp imagery, made for print.
        </p>
        <div style={{ display: 'flex', marginTop: 6, width: '100%', maxWidth: 300 }}>
          <button onClick={onOpenPoster} disabled={busy} style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '13px 14px', borderRadius: 999, border: 'none', cursor: busy ? 'progress' : 'pointer', background: T.cream, color: T.accentDeep, font: `500 13px/1 ${T.f.text}`, opacity: busy ? 0.7 : 1 }}>
            {I.dl({ s: 15 })} {busy ? 'Preparing…' : 'Download PDF'}
          </button>
        </div>
      </div>
    </Reveal>
  );
}

const cells = (ids: string[], captions: (string | undefined)[]): VsCell[] =>
  ids.map((cellId, i) => ({ cellId, caption: captions[i] }));

/* ── Hair result composition ───────────────────────────────────────────── */
export function HairResult({ result, atlasUrl, coords, imagePath, id, date, onOpenPoster, exporting }: {
  result: HairAnalysisResult; atlasUrl: string; coords: AtlasCoords; imagePath?: string; id: string; date: string;
  onOpenPoster?: () => void;
  exporting?: boolean;
}) {
  const bestStyles = (result.best_styles).map(s => s.name);
  const worstStyles = (result.less_flattering_styles).map(s => s.name);
  const lengths = result.best_lengths;
  const compareOptions: CompareOption[] = [
    ...(imagePath ? [{ id: 'photo', label: 'Your photo', photo: true } satisfies CompareOption] : []),
    ...lengths.slice(0, 3).map((l, i): CompareOption => ({ id: `len${i}`, label: l.name || `Length ${i + 1}`, cellId: ['A2', 'A3', 'A4'][i] })),
    ...bestStyles.slice(0, 5).map((n, i): CompareOption => ({ id: `style${i}`, label: n || `Style ${i + 1}`, cellId: ['B1', 'B2', 'B3', 'B4', 'B5'][i] })),
  ];
  const cmpBefore = imagePath ? 'photo' : compareOptions[0].id;
  const cmpAfter = lengths.length > 0 ? 'len0' : lastCompareId(compareOptions);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 38 }}>
      <Reveal y={0}>
        <Hero kind="hair" title="Your hair" em="report" id={id} date={date} imagePath={imagePath} atlasUrl={atlasUrl} coords={coords}
          stats={[
            { label: 'Hair type', value: result.hair_type.texture || '—' },
            { label: 'Density', value: result.hair_type.density || '—' },
            { label: 'Best parting', value: result.best_parting || '—' },
          ]} />
      </Reveal>

      {atlasUrl && lengths.length > 0 && (
        <Reveal>
          <SectionHead index="01" kicker="Drag to compare" title="See the difference" />
          <BeforeAfter options={compareOptions} defaultBeforeId={cmpBefore} defaultAfterId={cmpAfter} atlasUrl={atlasUrl} coords={coords} imagePath={imagePath} ratio={0.84} />
          <p style={{ margin: '12px 2px 0', font: `400 13px/1.5 ${T.f.text}`, color: T.ink45 }}>
            Pick any two looks to compare side by side, then drag the handle — all generated from your photo.
          </p>
        </Reveal>
      )}

      {result.hair_notes.length > 0 && (
        <Reveal>
          <SectionHead index="02" kicker="Read" title="Your hair profile" />
          <Card><MarkList items={result.hair_notes} /></Card>
        </Reveal>
      )}

      {lengths.length > 0 && (
        <Reveal>
          <SectionHead index="03" kicker="Three options" title="Best lengths" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {lengths.slice(0, 3).map((l, i) => (
              <Card key={i} pad={12} style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                <div style={{ width: 88, flexShrink: 0 }}>
                  <ZoomableAtlasCell atlasUrl={atlasUrl} coords={coords} cellId={['A2', 'A3', 'A4'][i]} caption={l.name} radius={12} style={{ border: `1px solid ${T.rule}` }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ font: `400 21px/1.05 ${T.f.display}`, color: T.ink, marginBottom: 4 }}>{l.name}</div>
                  {l.sub_label && <span style={{ display: 'inline-block', font: `500 9px/1 ${T.f.text}`, letterSpacing: '.14em', textTransform: 'uppercase', color: T.accentInk, background: T.accentSoft, padding: '5px 9px', borderRadius: 999, marginBottom: 8 }}>{l.sub_label}</span>}
                  {l.description && <div style={{ font: `400 13px/1.45 ${T.f.text}`, color: T.ink45 }}>{l.description}</div>}
                </div>
              </Card>
            ))}
          </div>
        </Reveal>
      )}

      {(bestStyles.length > 0 || worstStyles.length > 0) && (
        <Reveal>
          <SectionHead index="04" kicker="A vs B" title="Hairstyle comparison" />
          <Card>
            <VersusGrid atlasUrl={atlasUrl} coords={coords}
              aTitle="Best styles" aCells={cells(['B1', 'B2', 'B3', 'B4', 'B5'].slice(0, bestStyles.length), bestStyles)}
              bTitle="Less flattering" bCells={cells(['B6', 'B7', 'B8', 'B9'].slice(0, worstStyles.length), worstStyles)} />
          </Card>
        </Reveal>
      )}

      {(result.best_hair_colors.length > 0 || result.hair_color_comparison.length > 0 || result.less_flattering_hair_colors.length > 0) && (
        <Reveal>
          <SectionHead index="05" kicker="Tap to copy hex" title="Hair colours" />
          <Card style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
            {result.best_hair_colors.length > 0 && (
              <div><MiniLabel>Best colours</MiniLabel><SwatchRow items={result.best_hair_colors} /></div>
            )}
            {result.hair_color_comparison.length > 0 && (
              <>
                <div style={{ height: 1, background: T.rule }} />
                <div>
                  <MiniLabel>On you · comparison</MiniLabel>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
                    {result.hair_color_comparison.slice(0, 4).map((c, i) => (
                      <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <ZoomableAtlasCell atlasUrl={atlasUrl} coords={coords} cellId={['C1', 'C2', 'C3', 'C4'][i]} caption={c.name} radius={10} style={{ border: `1px solid ${T.rule}` }} />
                        <span style={{ font: `400 10px/1.2 ${T.f.text}`, color: T.ink70, textAlign: 'center' }}>{c.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
            {result.less_flattering_hair_colors.length > 0 && (
              <>
                <div style={{ height: 1, background: T.rule }} />
                <div><MiniLabel bad>Less flattering colours</MiniLabel><div style={{ opacity: .7 }}><SwatchRow items={result.less_flattering_hair_colors} /></div></div>
              </>
            )}
          </Card>
        </Reveal>
      )}

      {result.avoid_rules.length > 0 && (
        <Reveal>
          <SectionHead index="06" kicker="Steer clear" title="Things to avoid" />
          <Card style={{ background: hexA(T.coral, .08), borderColor: hexA(T.coral, .25) }}><MarkList items={result.avoid_rules} bad /></Card>
        </Reveal>
      )}

      <CTA onOpenPoster={onOpenPoster} busy={exporting} />
    </div>
  );
}

/* ── Makeup result composition ─────────────────────────────────────────── */
export function MakeupResult({ result, atlasUrl, coords, imagePath, id, date, onOpenPoster, exporting }: {
  result: MakeupAnalysisResult; atlasUrl: string; coords: AtlasCoords; imagePath?: string; id: string; date: string;
  onOpenPoster?: () => void;
  exporting?: boolean;
}) {
  const eyeBest = result.flattering_eye_looks;
  const eyeWorst = result.less_flattering_eye_looks;
  const lookBest = result.flattering_makeup_looks;
  const lookWorst = result.less_flattering_makeup_looks;
  const undertoneSwatches = result.undertone.swatches;
  const compareOptions: CompareOption[] = [
    ...(imagePath ? [{ id: 'photo', label: 'Bare', photo: true } satisfies CompareOption] : []),
    ...lookBest.slice(0, 6).map((n, i): CompareOption => ({ id: `look${i}`, label: n || `Look ${i + 1}`, cellId: ['C1', 'C2', 'C3', 'C4', 'C5', 'C6'][i] })),
    ...eyeBest.slice(0, 5).map((n, i): CompareOption => ({ id: `eye${i}`, label: n || `Eye ${i + 1}`, cellId: ['B1', 'B2', 'B3', 'B4', 'B5'][i] })),
  ];
  const cmpBefore = imagePath ? 'photo' : compareOptions[0].id;
  const cmpAfter = lookBest.length > 0 ? 'look0' : lastCompareId(compareOptions);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 38 }}>
      <Reveal y={0}>
        <Hero kind="makeup" title="Your makeup" em="report" id={id} date={date} imagePath={imagePath} atlasUrl={atlasUrl} coords={coords}
          stats={[
            { label: 'Undertone', value: result.undertone.classification || '—' },
            { label: 'Skin depth', value: result.skin_depth.level || '—' },
            { label: 'Contrast', value: result.contrast_level.level || '—' },
          ]} />
      </Reveal>

      {atlasUrl && lookBest.length > 0 && (
        <Reveal>
          <SectionHead index="01" kicker="Drag to compare" title="Bare vs styled" />
          <BeforeAfter options={compareOptions} defaultBeforeId={cmpBefore} defaultAfterId={cmpAfter} atlasUrl={atlasUrl} coords={coords} imagePath={imagePath} ratio={0.84} />
          <p style={{ margin: '12px 2px 0', font: `400 13px/1.5 ${T.f.text}`, color: T.ink45 }}>
            Pick any two looks to compare side by side, then drag the handle — each rendered on your own features.
          </p>
        </Reveal>
      )}

      <Reveal>
        <SectionHead index="02" kicker="Analysis" title="Your colour story" />
        <Card style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          {undertoneSwatches.length > 0 && (
            <div>
              <MiniLabel>Undertone</MiniLabel>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                {undertoneSwatches.map((s, i) => (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 54, height: 54, borderRadius: '50%', background: s.hex, border: `1px solid ${T.rule}`, boxShadow: T.shSoft }} />
                    <span style={{ font: `500 9px/1 ${T.f.mono}`, letterSpacing: '.1em', textTransform: 'uppercase', color: T.ink45 }}>{s.label}</span>
                  </div>
                ))}
                <div style={{ flex: 1, paddingLeft: 6 }}>
                  <div style={{ font: `400 18px/1.1 ${T.f.display}`, color: T.ink }}>{result.undertone.classification}</div>
                </div>
              </div>
            </div>
          )}
          <div style={{ height: 1, background: T.rule }} />
          <Meter label="Skin depth" value={result.skin_depth.level || ''} position={result.skin_depth.indicator_position} stops={['#F4E6D4', '#E7C9A9', '#C99B6E', '#9A6B43']} />
          <Meter label="Contrast level" value={result.contrast_level.level || ''} position={((result.contrast_level.step) - 0.5) / 5} stops={['#EFE7DC', '#C9BFAE', '#8A8170', '#3a352c']} />
          {result.best_features.length > 0 && (
            <>
              <div style={{ height: 1, background: T.rule }} />
              <div>
                <MiniLabel>Best features</MiniLabel>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {result.best_features.map((f, i) => (
                    <span key={i} style={{ font: `500 12px/1 ${T.f.text}`, color: T.accentInk, background: T.accentSoft, border: `1px solid ${hexA(T.accentBase, .28)}`, padding: '8px 12px', borderRadius: 999 }}>{f}</span>
                  ))}
                </div>
              </div>
            </>
          )}
        </Card>
      </Reveal>

      {(result.foundation_guidance.length > 0 || result.best_foundation_shades.length > 0) && (
        <Reveal>
          <SectionHead index="03" kicker="Base" title="Foundation & concealer" />
          <Card style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {result.foundation_guidance.length > 0 && <MarkList items={result.foundation_guidance} />}
            {result.best_foundation_shades.length > 0 && (
              <><div style={{ height: 1, background: T.rule }} /><div><MiniLabel>Best foundation shades</MiniLabel><SwatchRow items={result.best_foundation_shades} size={42} /></div></>
            )}
            {result.best_concealer_shades.length > 0 && (
              <div><MiniLabel>Best concealer shades</MiniLabel><SwatchRow items={result.best_concealer_shades} size={42} /></div>
            )}
            {result.best_finish && <FinishBar label="Best finish" value={result.best_finish} />}
          </Card>
        </Reveal>
      )}

      <Reveal>
        <SectionHead index="04" kicker="Eye makeup" title="Eyes" />
        <Card style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          {result.best_eyeshadow_colors.length > 0 && (
            <div><MiniLabel>Best eyeshadow colours · tap to copy</MiniLabel><SwatchRow items={result.best_eyeshadow_colors} size={40} /></div>
          )}
          {(eyeBest.length > 0 || eyeWorst.length > 0) && (
            <>
              <div style={{ height: 1, background: T.rule }} />
              <VersusGrid atlasUrl={atlasUrl} coords={coords}
                aTitle="Flattering eye looks" aCells={cells(['B1', 'B2', 'B3', 'B4', 'B5'].slice(0, eyeBest.length), eyeBest)}
                bTitle="Less flattering" bCells={cells(['B6', 'B7', 'B8', 'B9'].slice(0, eyeWorst.length), eyeWorst)} />
            </>
          )}
          {result.eyeliner_mascara.length > 0 && (
            <>
              <div style={{ height: 1, background: T.rule }} />
              <div>
                <MiniLabel>Eyeliner & mascara</MiniLabel>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {result.eyeliner_mascara.map((e, i) => (
                    <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: `500 12px/1 ${T.f.text}`, color: T.ink70, background: T.surface2, border: `1px solid ${T.rule}`, padding: '8px 12px', borderRadius: 999 }}>
                      <span style={{ color: T.accentInk, display: 'inline-flex' }}>{I.eye({ s: 13 })}</span>{e}
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}
        </Card>
      </Reveal>

      {(result.best_blush_shades.length > 0 || result.best_lip_shades.length > 0) && (
        <Reveal>
          <SectionHead index="05" kicker="Lips & blush" title="Colour on the face" />
          <Card style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
            {result.best_blush_shades.length > 0 && (
              <div><MiniLabel>Best blush shades</MiniLabel><SwatchRow items={result.best_blush_shades} size={44} /></div>
            )}
            {result.best_lip_shades.length > 0 && (
              <><div style={{ height: 1, background: T.rule }} /><div><MiniLabel>Best lip shades</MiniLabel><SwatchRow items={result.best_lip_shades} size={44} /></div></>
            )}
            {result.best_lip_finish && <FinishBar icon={I.lips({ s: 16 })} label="Best lip finish" value={result.best_lip_finish} />}
          </Card>
        </Reveal>
      )}

      {(lookBest.length > 0 || lookWorst.length > 0) && (
        <Reveal>
          <SectionHead index="06" kicker="Full looks" title="Looks comparison" />
          <Card style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <VersusGrid atlasUrl={atlasUrl} coords={coords}
              aTitle="Flattering looks" aCells={cells(['C1', 'C2', 'C3', 'C4', 'C5', 'C6'].slice(0, lookBest.length), lookBest)}
              bTitle="Less flattering" bCells={cells(['C7', 'C8', 'C9', 'C10'].slice(0, lookWorst.length), lookWorst)} />
            {result.best_accessories.length > 0 && (
              <>
                <div style={{ height: 1, background: T.rule }} />
                <div>
                  <MiniLabel>Best accessories</MiniLabel>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
                    {result.best_accessories.slice(0, 4).map((a, i) => (
                      <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <ZoomableAtlasCell atlasUrl={atlasUrl} coords={coords} cellId={['E8', 'E9', 'E10', 'E11'][i]} caption={a} radius={10} style={{ border: `1px solid ${T.rule}` }} />
                        <span style={{ font: `400 9.5px/1.2 ${T.f.text}`, color: T.ink70, textAlign: 'center' }}>{a}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </Card>
        </Reveal>
      )}

      <CTA onOpenPoster={onOpenPoster} busy={exporting} />
    </div>
  );
}
